import {
  UploadPreparedV1Schema,
  type EntityTypeV1,
  type FileTransferV1,
  type PresenceV1,
  type UploadPreparedV1,
} from '@mlo-local/protocol';
import { create } from '@bufbuild/protobuf';
import type {
  ClientCreateInput,
  ClientListRequest,
  ClientUpdateInput,
  ClientView,
  DocumentUploadRequest,
  DocumentView,
  WorkspaceTransport,
} from './foundation.js';
import { randomUuid } from './uuid.js';
import { RemoteConflictError } from './remoteTransport.js';

type BridgeResponse = {
  id: string;
  ok: boolean;
  result?: Record<string, unknown>;
  error?: { code?: string; message?: string };
};

type LocalBridge = {
  postMessage(message: string): void;
  addEventListener(type: 'message', listener: (event: MessageEvent<string>) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent<string>) => void): void;
};

declare global {
  interface Window {
    mloLocalBridge?: LocalBridge;
  }
}

export function hasLocalBridge(): boolean {
  return typeof window !== 'undefined' && window.mloLocalBridge !== undefined;
}

/** JSON adapter for the embedded Android bridge; it intentionally has no browser storage. */
export class LocalAppTransport implements WorkspaceTransport {
  #status: 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED' = 'DISCONNECTED';
  #listeners = new Set<(clients: readonly ClientView[]) => void>();
  #documentListeners = new Map<string, Set<(documents: readonly DocumentView[]) => void>>();
  #clients: readonly ClientView[] = [];
  #documents = new Map<string, readonly DocumentView[]>();
  #listenerAttached = true;
  #pending = new Map<
    string,
    { resolve: (response: BridgeResponse) => void; reject: (error: Error) => void }
  >();
  readonly #bridge: LocalBridge;
  readonly #onMessage = (event: MessageEvent<string>) => {
    let response: BridgeResponse;
    try {
      response = JSON.parse(event.data) as BridgeResponse;
    } catch {
      return;
    }
    const pending = this.#pending.get(response.id);
    if (!pending) return;
    this.#pending.delete(response.id);
    if (response.ok) pending.resolve(response);
    else pending.reject(new Error(response.error?.code || 'LOCAL_UI_FAILED'));
  };

  constructor(
    bridge: LocalBridge = window.mloLocalBridge ??
      (() => {
        throw new Error('ANDROID_BRIDGE_UNAVAILABLE');
      })(),
  ) {
    this.#bridge = bridge;
    this.#bridge.addEventListener('message', this.#onMessage);
  }

  get status(): 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED' {
    return this.#status;
  }

  async connect(): Promise<void> {
    if (this.#status === 'CONNECTED') return;
    if (!this.#listenerAttached) {
      this.#bridge.addEventListener('message', this.#onMessage);
      this.#listenerAttached = true;
    }
    this.#status = 'CONNECTING';
    const ready = await this.send('ready');
    if (ready.result?.actorSelected !== true) {
      this.#status = 'DISCONNECTED';
      this.emitClients();
      return;
    }
    this.#status = 'CONNECTED';
    this.emitClients();
  }

  async listClients(request: ClientListRequest = {}): Promise<readonly ClientView[]> {
    const result = await this.requireConnected('listClients', request);
    this.#clients = (result.clients ?? []) as ClientView[];
    this.emitClients();
    return this.#clients;
  }

  async createClient(input: ClientCreateInput): Promise<ClientView> {
    const result = await this.requireConnected('createClient', input);
    const client = result.client as ClientView;
    this.#clients = [...this.#clients, client];
    this.emitClients();
    return client;
  }

  async updateClient(input: ClientUpdateInput): Promise<ClientView> {
    const result = await this.requireConnected('updateClient', input);
    const client = result.client as ClientView;
    this.#clients = this.#clients.map((current) => (current.id === client.id ? client : current));
    this.emitClients();
    return client;
  }

  async archiveClient(input: { id: string; baseRevision: number }): Promise<ClientView> {
    const result = await this.requireConnected('archiveClient', input);
    const client = result.client as ClientView;
    this.#clients = this.#clients.map((current) => (current.id === client.id ? client : current));
    this.emitClients();
    return client;
  }

  async listDocuments(clientId: string): Promise<readonly DocumentView[]> {
    const result = await this.requireConnected('listDocuments', { clientId, pageSize: 100 });
    const documents = (result.documents ?? []) as DocumentView[];
    this.#documents.set(clientId, documents);
    this.#documentListeners.get(clientId)?.forEach((listener) => listener(documents));
    return documents;
  }

  subscribe(listener: (clients: readonly ClientView[]) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  subscribeDocuments(
    clientId: string,
    listener: (documents: readonly DocumentView[]) => void,
  ): () => void {
    const listeners = this.#documentListeners.get(clientId) ?? new Set();
    listeners.add(listener);
    this.#documentListeners.set(clientId, listeners);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) this.#documentListeners.delete(clientId);
    };
  }

  subscribeFile(listener: (transfer: FileTransferV1) => void): () => void {
    return () => {
      void listener;
    };
  }

  subscribePresence(listener: (presence: PresenceV1) => void): () => void {
    return () => {
      void listener;
    };
  }

  async prepareDocumentUpload(
    input: DocumentUploadRequest,
  ): Promise<{ operationId: string; prepared: UploadPreparedV1 }> {
    const operationId = randomUuid();
    const result = await this.requireConnected('prepareDocumentUpload', {
      clientId: input.clientId,
      operationId,
      displayName: input.displayName ?? input.file.name,
      category: input.category ?? 'GENERAL',
      notes: input.notes ?? '',
      originalFileName: input.file.name,
      declaredMime: input.file.type || 'application/octet-stream',
      declaredSize: input.file.size,
    });
    const prepared = result.prepared as Record<string, unknown>;
    return {
      operationId,
      prepared: create(UploadPreparedV1Schema, {
        transferId: base64ToBytes(String(prepared.transferId)),
        documentId: base64ToBytes(String(prepared.documentId)),
        fileVersionId: base64ToBytes(String(prepared.fileVersionId)),
        chunkSize: Number(prepared.chunkSize),
        expiresAtMs: BigInt(prepared.expiresAtMs as number),
      }),
    };
  }

  async uploadDocument(
    operationId: string,
    prepared: UploadPreparedV1,
    file: File,
  ): Promise<DocumentView> {
    const plaintext = new Uint8Array(await file.arrayBuffer());
    const plaintextSha256 = new Uint8Array(await crypto.subtle.digest('SHA-256', plaintext));
    let index = 0;
    for (let offset = 0; offset < plaintext.byteLength; offset += prepared.chunkSize) {
      const chunk = plaintext.slice(offset, offset + prepared.chunkSize);
      const chunkSha256 = new Uint8Array(await crypto.subtle.digest('SHA-256', chunk));
      await this.requireConnected('uploadChunk', {
        transferId: bytesToUuid(prepared.transferId),
        index,
        bytes: bytesToBase64(chunk),
        chunkSha256: bytesToBase64(chunkSha256),
      });
      index += 1;
    }
    const result = await this.requireConnected('commitDocumentUpload', {
      operationId,
      transferId: bytesToUuid(prepared.transferId),
      documentId: bytesToUuid(prepared.documentId),
      fileVersionId: bytesToUuid(prepared.fileVersionId),
      totalChunks: index,
      plaintextSha256: bytesToBase64(plaintextSha256),
    });
    const document = result.document as DocumentView;
    const current = this.#documents.get(document.clientId) ?? [];
    this.#documents.set(document.clientId, [
      ...current.filter((value) => value.id !== document.id),
      document,
    ]);
    this.#documentListeners
      .get(document.clientId)
      ?.forEach((listener) => listener(this.#documents.get(document.clientId) ?? []));
    return document;
  }

  async downloadDocument(
    fileVersionId: string,
  ): Promise<{ file: Blob; name: string; mimeType: string }> {
    const result = await this.requireConnected('downloadDocument', { fileVersionId });
    const chunks = (result.chunks as Array<{ index: number; bytes: string }>)
      .slice()
      .sort((left, right) => left.index - right.index);
    const decoded = chunks.map((chunk) => base64ToBytes(chunk.bytes));
    const bytes = new Uint8Array(decoded.reduce((total, chunk) => total + chunk.byteLength, 0));
    let offset = 0;
    decoded.forEach((chunk) => {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    });
    const expectedDigest = base64ToBytes(asString(result.plaintextSha256));
    const actualDigest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
    if (!equalBytes(actualDigest, expectedDigest)) throw new Error('DOWNLOAD_DIGEST');
    return {
      file: new Blob([bytes], { type: asString(result.mimeType, 'application/octet-stream') }),
      name: asString(result.name, 'download'),
      mimeType: asString(result.mimeType, 'application/octet-stream'),
    };
  }

  announceViewing(entityType: EntityTypeV1, entityId: string): Promise<void> {
    void entityType;
    void entityId;
    if (this.#status !== 'CONNECTED') return Promise.reject(new Error('SESSION_NOT_CONNECTED'));
    return Promise.resolve();
  }

  async closeLocalSession(): Promise<void> {
    if (this.#status === 'CONNECTED') await this.send('closeSession');
    this.close();
  }

  close(): void {
    this.#pending.forEach(({ reject }) => reject(new Error('SESSION_CLOSED')));
    this.#pending.clear();
    this.#bridge.removeEventListener('message', this.#onMessage);
    this.#listenerAttached = false;
    this.#clients = [];
    this.#documents.clear();
    this.#status = 'DISCONNECTED';
    this.emitClients();
    this.#documentListeners.clear();
  }

  private async requireConnected(
    type: string,
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    if (this.#status !== 'CONNECTED') throw new Error('SESSION_NOT_CONNECTED');
    const result = (await this.send(type, payload)).result ?? {};
    if (result.conflict) {
      throw new RemoteConflictError(
        result.conflict as ClientView,
        asString(result.rejectedOperationId),
      );
    }
    return result;
  }

  private send(type: string, payload: Record<string, unknown> = {}): Promise<BridgeResponse> {
    const id = randomUuid();
    return new Promise((resolve, reject) => {
      this.#pending.set(id, { resolve, reject });
      this.#bridge.postMessage(JSON.stringify({ id, type, payload }));
      globalThis.setTimeout(() => {
        const pending = this.#pending.get(id);
        if (!pending) return;
        this.#pending.delete(id);
        pending.reject(new Error('LOCAL_UI_TIMEOUT'));
      }, 10_000);
    });
  }

  private emitClients(): void {
    this.#listeners.forEach((listener) => listener(this.#clients));
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function base64ToBytes(value: string): Uint8Array {
  return Uint8Array.from(atob(value), (c) => c.charCodeAt(0));
}

function bytesToUuid(value: Uint8Array): string {
  if (value.byteLength !== 16) throw new Error('INVALID_UUID_BYTES');
  const hex = [...value].map((part) => part.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function equalBytes(left: Uint8Array, right: Uint8Array): boolean {
  return (
    left.byteLength === right.byteLength && left.every((value, index) => value === right[index])
  );
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}
